import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEllipsisV, faEye, faEdit, faTrash, faCheck, faUserSlash, faUndo, faUserPlus,
    faCircleCheck, faCircleXmark, faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { whatsappUrl } from '../../../utils/whatsapp';
import { Button, Tooltip } from '@material-tailwind/react';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import BlacklistModal from './BlacklistModal';
import CreatePortalAccountModal from '../../common/CreatePortalAccountModal';

const MENU_WIDTH = 176;
const MENU_GAP = 8;
const MENU_MARGIN = 12;
const MENU_ESTIMATED_HEIGHT = 220;

const getLicenseStatus = (expiryDate) => {
    if (!expiryDate || expiryDate === 'no licence exp') {
        return { color: 'text-gray-600 bg-gray-50 px-2 py-1 rounded-sm' };
    }
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry < 0) return { color: 'text-red-800 font-bold bg-red-100 px-2 py-1 rounded-sm border border-red-200' };
    if (daysUntilExpiry <= 30) return { color: 'text-orange-700 font-semibold bg-orange-50 px-2 py-1 rounded-sm' };
    return { color: 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-sm' };
};

const getBalanceStatus = (balance) => {
    const bal = parseFloat(balance) || 0;
    if (bal < 0) return 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-sm';
    if (bal <= 1000) return 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-sm';
    if (bal <= 3000) return 'text-blue-700 font-semibold bg-blue-50 px-2 py-1 rounded-sm';
    if (bal <= 5000) return 'text-amber-700 font-semibold bg-amber-50 px-2 py-1 rounded-sm';
    if (bal <= 10000) return 'text-purple-700 font-semibold bg-purple-50 px-2 py-1 rounded-sm';
    if (bal <= 15000) return 'text-red-700 font-semibold bg-red-50 px-2 py-1 rounded-sm';
    return 'text-red-800 font-bold bg-red-100 px-2 py-1 rounded-sm border border-red-200';
};

const getStatusBadgeClass = (status) => {
    switch (status) {
        case 'AVAILABLE': return 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-sm';
        case 'ASSIGNED': return 'text-orange-700 font-semibold bg-orange-50 px-2 py-1 rounded-sm';
        case 'OFF DUTY': return 'text-gray-700 font-medium bg-gray-50 px-2 py-1 rounded-sm';
        default: return 'text-red-700 font-semibold bg-red-50 px-2 py-1 rounded-sm';
    }
};

const getVerificationBadgeClass = (status) => {
    if (!status) return 'text-gray-700 font-medium bg-gray-50 px-2 py-1 rounded-sm border border-gray-200';
    switch ((status || '').toString().toUpperCase()) {
        case 'VERIFIED': return 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-sm';
        case 'PENDING': return 'text-amber-700 font-semibold bg-amber-50 px-2 py-1 rounded-sm';
        case 'FAILED': return 'text-red-700 font-semibold bg-red-50 px-2 py-1 rounded-sm';
        default: return 'text-gray-700 font-medium bg-gray-50 px-2 py-1 rounded-sm border border-gray-200';
    }
};

const getLicenseVerifiedClass = (verified) => {
    if (verified === true || verified === 'true' || verified === '1' || verified === 1)
        return 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-sm';
    return 'text-gray-700 font-medium bg-gray-50 px-2 py-1 rounded-sm border border-gray-200';
};

const formatDateHuman = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d)) return iso;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) { return iso; }
};

export default function DriversTable({
    drivers = [],
    onEdit = () => {},
    onView = () => {},
    onDelete = () => {},
    onVerify = () => {},
    onRefresh = null,
    onBlacklist = null,
}) {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [perms, setPerms] = useState([]);
    const [openMenu, setOpenMenu] = useState(null);
    const buttonRefs = useRef({});
    const menuPortalRef = useRef(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalDriver, setModalDriver] = useState(null);
    const [modalAction, setModalAction] = useState('blacklist');
    const [modalError, setModalError] = useState('');
    const [portalModalOpen, setPortalModalOpen] = useState(false);
    const [portalModalDriver, setPortalModalDriver] = useState(null);

    useEffect(() => {
        if (!token) return;
        fetch(`${rootUrl}/api/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(j => setPerms(j.data || []))
            .catch(() => setPerms([]));
    }, [token]);

    const canBlacklist = () => perms.includes('drivers.blacklist') || perms.includes('roles.manage');
    const canManageUsers = () => perms.includes('users.manage') || perms.includes('roles.manage');

    const positionMenu = (driverId) => {
        const button = buttonRefs.current[driverId];
        if (!button) return null;
        const rect = button.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - MENU_MARGIN;
        const spaceAbove = rect.top - MENU_MARGIN;
        const openUp = spaceBelow < MENU_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;
        const top = openUp ? rect.top - MENU_GAP : rect.bottom + MENU_GAP;
        const left = Math.max(
            MENU_MARGIN,
            Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - MENU_MARGIN)
        );
        return { top, left, openUp };
    };

    const openActionMenu = (driverId) => {
        setOpenMenu((current) => {
            if (current?.driverId === driverId) return null;
            const position = positionMenu(driverId);
            if (!position) return current;
            return { driverId, ...position };
        });
    };

    const closeActionMenu = () => setOpenMenu(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!openMenu) return;
            const activeMenu = menuPortalRef.current;
            const activeButton = buttonRefs.current[openMenu.driverId];
            if (!activeMenu?.contains(event.target) && !activeButton?.contains(event.target)) {
                closeActionMenu();
            }
        };
        const handleEscape = (event) => { if (event.key === 'Escape') closeActionMenu(); };
        const handleReposition = () => {
            if (!openMenu?.driverId) return;
            const position = positionMenu(openMenu.driverId);
            if (!position) return;
            setOpenMenu((current) => current?.driverId === openMenu.driverId ? { ...current, ...position } : current);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', handleReposition);
        window.addEventListener('scroll', handleReposition, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', handleReposition);
            window.removeEventListener('scroll', handleReposition, true);
        };
    }, [openMenu]);

    const handleConfirmBlacklist = async ({ reason }) => {
        if (!modalDriver) return;
        if (typeof onBlacklist !== 'function') { setModalError('Blacklist handler not configured'); return; }
        try {
            const result = await onBlacklist(modalDriver.id, modalAction, reason);
            if (!result || !result.success) { setModalError(result?.message || 'Action failed'); return; }
            if (typeof onRefresh === 'function') { try { onRefresh(); } catch (e) {} }
            setModalOpen(false);
            setModalDriver(null);
            setModalError('');
        } catch (err) {
            setModalError(err?.message || 'Action failed');
        }
    };

    const sortedDrivers = [...drivers].sort((a, b) => new Date(b.input_date || 0) - new Date(a.input_date || 0));

    const actionMenu = openMenu && typeof document !== 'undefined' ? createPortal(
        <div
            ref={menuPortalRef}
            className="fixed min-w-[176px] overflow-hidden rounded-sm border border-slate-200 bg-white py-1 text-left shadow-2xl"
            style={{
                top: `${openMenu.top}px`,
                left: `${openMenu.left}px`,
                zIndex: 99999,
                transform: openMenu.openUp ? 'translateY(-100%)' : 'none',
            }}
        >
            {(() => {
                const driver = sortedDrivers.find(d => d.id === openMenu.driverId);
                if (!driver) return null;
                return (
                    <>
                        <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => { closeActionMenu(); navigate(`/drivers/${driver.id_no}`); }}
                        >
                            <FontAwesomeIcon icon={faEye} className="w-3" />
                            View
                        </button>
                        <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => { closeActionMenu(); onEdit(driver.id); }}
                        >
                            <FontAwesomeIcon icon={faEdit} className="w-3" />
                            Edit
                        </button>
                        <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                            onClick={() => { closeActionMenu(); onVerify(driver.id); }}
                        >
                            <FontAwesomeIcon icon={faCheck} className="w-3" />
                            Verify License
                        </button>
                        {!driver.portal_user_id && canManageUsers() && (
                            <button
                                type="button"
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                                onClick={() => { closeActionMenu(); setPortalModalDriver(driver); setPortalModalOpen(true); }}
                            >
                                <FontAwesomeIcon icon={faUserPlus} className="w-3" />
                                Create Account
                            </button>
                        )}
                        {canBlacklist() && (
                            <button
                                type="button"
                                className={`flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-slate-100 ${driver.is_blacklisted ? 'text-green-700' : 'text-amber-700'}`}
                                onClick={() => {
                                    closeActionMenu();
                                    setModalDriver(driver);
                                    setModalAction(driver.is_blacklisted ? 'unblacklist' : 'blacklist');
                                    setModalError('');
                                    setModalOpen(true);
                                }}
                            >
                                <FontAwesomeIcon icon={driver.is_blacklisted ? faUndo : faUserSlash} className="w-3" />
                                {driver.is_blacklisted ? 'Unblacklist' : 'Blacklist'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                            onClick={() => { closeActionMenu(); onDelete(driver.id); }}
                        >
                            <FontAwesomeIcon icon={faTrash} className="w-3" />
                            Delete
                        </button>
                    </>
                );
            })()}
        </div>,
        document.body
    ) : null;

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] table-auto text-left">
                <thead>
                    <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                        <th className="p-2 text-xs font-semibold text-brand-secondary">Driver Name</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary">Phone</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary hidden lg:table-cell">ID No</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary hidden md:table-cell">License No</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary hidden md:table-cell">License Verified</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary hidden lg:table-cell">Expiry</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary">Status</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary text-right hidden xl:table-cell">Balance</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary hidden lg:table-cell">Portal</th>
                        <th className="p-2 text-xs font-semibold text-brand-secondary w-10 text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedDrivers.map((driver) => (
                        <tr
                            key={driver.id}
                            className="border-b border-blue-gray-50 even:bg-gray-50/50 hover:bg-blue-gray-50/50 transition-colors duration-200"
                        >
                            <td className="p-2 text-xs font-medium text-gray-800">{driver.name}</td>
                            <td className="p-2 text-xs text-gray-600">
                                {driver.phone && driver.phone !== 'No phone provided' ? (
                                    <a
                                        href={whatsappUrl(driver.phone)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-green-700 hover:underline"
                                    >
                                        <FontAwesomeIcon icon={faWhatsapp} className="text-green-600" />
                                        {driver.phone}
                                    </a>
                                ) : (
                                    <span className="text-gray-400">{driver.phone}</span>
                                )}
                            </td>
                            <td className="p-2 text-xs text-gray-600 hidden lg:table-cell">{driver.id_no}</td>
                            <td className="p-2 text-xs text-gray-600 hidden md:table-cell">{driver.license_no}</td>
                            <td className="p-2 hidden md:table-cell">
                                {driver.license_verification_status ? (
                                    <a
                                        href={driver.license_sticker_link || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={driver.license_sticker_link ? 'text-blue-600 hover:underline' : 'text-gray-700'}
                                    >
                                        <span className={`text-xs ${getVerificationBadgeClass(driver.license_verification_status)}`}>
                                            {driver.license_verification_status}
                                        </span>
                                    </a>
                                ) : (
                                    <span className={`text-xs ${getLicenseVerifiedClass(driver.license_verified)}`}>
                                        {driver.license_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                )}
                            </td>
                            <td className="p-2 hidden lg:table-cell">
                                <span className={`text-xs ${getLicenseStatus(driver.license_expiry).color}`}>
                                    {driver.license_expiry && driver.license_expiry !== 'no licence exp'
                                        ? formatDateHuman(driver.license_expiry)
                                        : 'No expiry'}
                                </span>
                            </td>
                            <td className="p-2">
                                <span className={`text-xs ${getStatusBadgeClass(driver.status)}`}>
                                    {driver.status || 'Unknown'}
                                </span>
                            </td>
                            <td className="p-2 text-right hidden xl:table-cell">
                                <span className={`text-xs ${getBalanceStatus(driver.bal)}`}>
                                    {parseFloat(driver.bal || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </td>
                            <td className="p-2 hidden lg:table-cell text-center">
                                {driver.portal_user_id ? (
                                    driver.portal_first_login_at ? (
                                        <Tooltip content="Portal account — activated" placement="top">
                                            <FontAwesomeIcon icon={faCircleCheck} className="text-green-600 text-base cursor-default" />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip content="Portal account — not yet activated" placement="top">
                                            <FontAwesomeIcon icon={faHourglassHalf} className="text-amber-500 text-base cursor-default" />
                                        </Tooltip>
                                    )
                                ) : (
                                    <Tooltip content="No portal account" placement="top">
                                        <FontAwesomeIcon icon={faCircleXmark} className="text-gray-300 text-base cursor-default" />
                                    </Tooltip>
                                )}
                            </td>
                            <td className="p-2 text-center">
                                <div className="flex justify-center">
                                    <Button
                                        ref={(node) => {
                                            if (node) buttonRefs.current[driver.id] = node;
                                            else delete buttonRefs.current[driver.id];
                                        }}
                                        size="sm"
                                        variant="outlined"
                                        className="flex h-8 w-8 items-center justify-center rounded-full p-0 text-slate-500 border border-slate-300 hover:bg-slate-100 hover:border-slate-400 shadow-none"
                                        onClick={() => openActionMenu(driver.id)}
                                    >
                                        <FontAwesomeIcon icon={faEllipsisV} className="text-sm" />
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {actionMenu}
            <BlacklistModal
                open={modalOpen}
                onClose={() => { setModalOpen(false); setModalError(''); }}
                onConfirm={handleConfirmBlacklist}
                driver={modalDriver}
                action={modalAction}
                error={modalError}
            />
            <CreatePortalAccountModal
                isOpen={portalModalOpen}
                onClose={() => { setPortalModalOpen(false); setPortalModalDriver(null); }}
                onSuccess={() => { if (typeof onRefresh === 'function') onRefresh(); }}
                actorType="driver"
                entityId={portalModalDriver?.id}
                prefill={{ name: portalModalDriver?.name, email: portalModalDriver?.email }}
                token={token}
            />
        </div>
    );
}
